/**
 * 腾讯数智人 云渲染 H5 SDK 封装
 *
 * 参考官方 demo 流程：setPrivatization → init → createSessionByAsset → startSession
 * 驱动方式：driverType 3（音频驱动），通过端渲染驱动接口获取流式音频后喂给云渲染数字人
 * 播报方式：大模型流式文本 → stream-tts(STREAM_TEXT) → PCM 音频 → IVH.play(audio)
 *
 * 外部 API 保持不变：beginStream / feedStreamDelta / flushStream / interruptAndClear
 */

import { connectStreamTTS } from './stream-tts-client.js'

const config = {
  virtualmanProjectId: import.meta.env.VITE_DIGITAL_HUMAN_PROJECT_ID   || '',
  assetVirtualmanKey:  import.meta.env.VITE_DIGITAL_HUMAN_ASSET_KEY    || '',
  sign:                import.meta.env.VITE_DIGITAL_HUMAN_SIGN         || '',
}

let _ready = false
let _sessionId = ''
let _userId = ''
let _lastAudioAt = 0
const PLAYBACK_GRACE_MS = 3 * 60 * 1000

function markAudioActivity() {
  _lastAudioAt = Date.now()
}

function markSessionDropped() {
  _ready = false
  _sessionId = ''
  _stopTTSOnSessionDrop()
}

// 文本缓冲（收集 LLM token 到合适粒度再发给 stream-tts）
let _streamBuffer = ''
let _flushTimer = null
let _textSeq = 0

// 音频序号（IVH.play audio command 要求 seq 从 1 开始）
let _audioSeq = 0
let _audioFinalized = false

// stream-tts WebSocket 连接
let _ttsHandle = null
let _ttsConnecting = false
const _ttsPending = []

const STREAM_MIN_CHARS = 20
const STREAM_FLUSH_INTERVAL = 1200

function getIVH() {
  return (typeof window !== 'undefined' && window.IVH) ? window.IVH : null
}

// ─── 初始化 & 会话 ───

export function init(element, opts = {}) {
  const IVH = getIVH()
  if (!IVH) {
    console.error('[CloudSDK] window.IVH not found')
    return false
  }

  if (opts.userId) _userId = opts.userId

  const el = typeof element === 'string'
    ? document.querySelector(element)
    : element
  if (!el) {
    console.error('[CloudSDK] element not found:', element)
    return false
  }

  const initOpts = { element: el, sign: config.sign }
  const assetKey = opts.assetVirtualmanKey || config.assetVirtualmanKey
  const projectId = opts.virtualmanProjectId || config.virtualmanProjectId
  if (assetKey) initOpts.assetVirtualmanKey = assetKey
  if (projectId) initOpts.virtualmanProjectId = projectId
  // isAreaStyle 仅影响注入样式的前半段；后半段仍会带 .tcplayer 定位与 video 的 object-fit:cover，
  // 真正对齐需在页面层覆盖 .tcplayer / video（见 ZaoshengWidget 中 ivh-model-container 样式）。
  if (opts.isAreaStyle === true) initOpts.isAreaStyle = true

  console.log('[CloudSDK] init, element:', el.id || el, 'userId:', _userId)
  IVH.init(initOpts)

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (_ready || _sessionId) {
        try { IVH.closeSession() } catch (_) {}
      }
    })
  }

  return true
}

export function registerEvents(callbacks = {}) {
  const IVH = getIVH()
  if (!IVH) return

  IVH.on('ready', () => {
    console.log('[CloudSDK] event: ready')
    callbacks.onReady?.()
  })

  IVH.on('error', (error) => {
    const msg = typeof error === 'object'
      ? (error.message || error.code || JSON.stringify(error))
      : String(error)
    console.error('[CloudSDK] event: error -', msg, error)
    if (error && (error.code === 110014 || /SessionNotReady/i.test(String(error.message || '')))) {
      markSessionDropped()
    }
    if (error && (error.code === 110015 || /WrongTiming|WaitingAudioOver/i.test(String(error.message || '')))) {
      _audioFinalized = true
    }
    callbacks.onError?.(error)
  })

  IVH.on('sessionReady', () => {
    console.log('[CloudSDK] event: sessionReady')
    callbacks.onSessionReady?.()
  })

  IVH.on('canplay', () => {
    console.log('[CloudSDK] event: canplay')
    try { IVH.setVolume(1) } catch (_) {}
    try {
      const player = window.ivhTcplayer
      if (player && typeof player.muted === 'function') player.muted(false)
      if (player && typeof player.volume === 'function') player.volume(1)
    } catch (_) {}
    callbacks.onCanplay?.()
  })

  IVH.on('play', () => {
    console.log('[CloudSDK] event: play')
    callbacks.onPlay?.()
  })

  IVH.on('playStreamStart', () => {
    console.log('[CloudSDK] event: playStreamStart')
    callbacks.onPlayStreamStart?.()
  })

  IVH.on('playStreamStop', () => {
    console.log('[CloudSDK] event: playStreamStop')
    callbacks.onPlayStreamStop?.()
  })

  IVH.on('sessionStart', () => {
    console.log('[CloudSDK] event: sessionStart')
    callbacks.onSessionStart?.()
  })

  IVH.on('sessionEnd', () => {
    console.log('[CloudSDK] event: sessionEnd')
    markSessionDropped()
    callbacks.onSessionEnd?.()
  })

  IVH.on('socket', (e) => {
    if (e.speakStatus === 'AudioStart' || e.speakStatus === 'TextStart') {
      console.log('[CloudSDK] event: socket', e.speakStatus)
      callbacks.onSpeakStart?.()
    } else if (e.speakStatus === 'AudioOver' || e.speakStatus === 'TextOver') {
      console.log('[CloudSDK] event: socket', e.speakStatus)
      callbacks.onSpeakEnd?.()
    }
  })
}

/**
 * 建流 + 开启会话
 * driverType: 3（音频驱动），外部通过 stream-tts 获取 PCM 音频后驱动口型
 */
export async function createAndStart(opts = {}) {
  const IVH = getIVH()
  if (!IVH) return null

  if (_ready || _sessionId) {
    console.log('[CloudSDK] closing previous session')
    try { await IVH.closeSession() } catch (_) {}
    _ready = false
    _sessionId = ''
  }

  try {
    const assetKey = config.assetVirtualmanKey
    const projectId = config.virtualmanProjectId
    const userId = opts.userId || _userId

    if (!userId) {
      console.error('[CloudSDK] userId is required')
      return null
    }

    let result
    if (assetKey) {
      console.log('[CloudSDK] createSessionByAsset, driverType: 3 (音频驱动), userId:', userId)
      result = await IVH.createSessionByAsset({
        driverType: 3,
        userId,
      })
    } else if (projectId) {
      result = await IVH.createSession({
        driverType: 3,
        userId,
      })
    } else {
      console.error('[CloudSDK] No assetVirtualmanKey or virtualmanProjectId')
      return null
    }

    if (!result) {
      console.error('[CloudSDK] createSession returned null')
      return null
    }

    _sessionId = result.sessionId
    console.log('[CloudSDK] session created:', _sessionId)

    const startOk = await IVH.startSession()
    if (startOk) {
      _ready = true
      try { IVH.setVolume(1) } catch (_) {}
      console.log('[CloudSDK] session started (audio-driven)')
    } else {
      console.error('[CloudSDK] startSession failed')
    }

    return result
  } catch (e) {
    console.error('[CloudSDK] createAndStart error:', e)
    return null
  }
}

// ─── 流式音频驱动 ───
// 外部调用方式不变（beginStream / feedStreamDelta / flushStream），
// 内部：文本 → stream-tts WebSocket → PCM 音频 → IVH.play({ command:'audio' })

function _sendAudioToIVH(base64Audio, isFinal = false) {
  const IVH = getIVH()
  if (!IVH || !_ready || _audioFinalized) return
  markAudioActivity()
  _audioSeq++
  if (isFinal) _audioFinalized = true
  try {
    const p = IVH.play({
      command: 'audio',
      data: base64Audio,
      seq: _audioSeq,
      isFinal,
    })
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        if (!_audioFinalized) {
          console.warn('[CloudSDK] IVH.play failed, stopping audio feed')
          _audioFinalized = true
        }
      })
    }
  } catch (e) {
    console.warn('[CloudSDK] IVH.play threw:', e.message || e)
    _audioFinalized = true
  }
}

function _stopTTSOnSessionDrop() {
  if (_ttsHandle) { try { _ttsHandle.close() } catch (_) {} _ttsHandle = null }
  _ttsConnecting = false
  _ttsPending.length = 0
  _streamBuffer = ''
  if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null }
}

function _stripMarkdownForTTS(text) {
  if (!text) return text
  return text
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^>\s*/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\*+/g, '')
    .replace(/`+/g, '')
    .replace(/~~/g, '')
    .replace(/\[(\d+)\]/g, '')
    .replace(/\$+/g, '')
    .replace(/\|/g, ' ')
    .replace(/\n{2,}/g, '\n')
}

function _sendTextToTTS(text, isFinal) {
  const cleaned = _stripMarkdownForTTS(text)
  if (!cleaned && !isFinal) return
  if (_ttsHandle) {
    _ttsHandle.sendText(cleaned, isFinal)
  } else if (_ttsConnecting) {
    _ttsPending.push({ text: cleaned, isFinal })
  }
}

/**
 * 开始新一轮流式播报：打开 stream-tts WebSocket，准备接收音频
 */
export function beginStream() {
  interruptAndClear()
  markAudioActivity()
  _textSeq = 0
  _audioSeq = 0
  _streamBuffer = ''
  _ttsConnecting = true
  _ttsPending.length = 0

  connectStreamTTS({
    onAudio(msg) {
      _audioFinalized = false
      _sendAudioToIVH(msg.audio, false)
    },
    onDone() {
      _sendAudioToIVH('', true)
      _audioSeq = 0
      _ttsHandle = null
      console.log('[CloudSDK] 音频流结束，已发送 isFinal')
    },
    onError(err) {
      console.error('[CloudSDK] stream-tts 错误:', err.message)
      _ttsHandle = null
      _ttsConnecting = false
    },
  }).then(handle => {
    _ttsHandle = handle
    _ttsConnecting = false
    for (const item of _ttsPending) {
      handle.sendText(item.text, item.isFinal)
    }
    _ttsPending.length = 0
    console.log('[CloudSDK] stream-tts 已连接')
  }).catch(err => {
    console.error('[CloudSDK] stream-tts 连接失败:', err.message)
    _ttsConnecting = false
  })
}

/**
 * 喂入 LLM 流式增量文本，按粒度缓冲后发给 stream-tts
 */
export function feedStreamDelta(delta) {
  if (!_ready || !delta) return
  markAudioActivity()
  _streamBuffer += delta

  const shouldFlush =
    _streamBuffer.length >= STREAM_MIN_CHARS ||
    /[。！？；\n]/.test(_streamBuffer)

  if (shouldFlush) {
    if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null }
    _sendTextToTTS(_streamBuffer, false)
    _streamBuffer = ''
  } else {
    if (_flushTimer) clearTimeout(_flushTimer)
    _flushTimer = setTimeout(() => {
      if (_streamBuffer) {
        _sendTextToTTS(_streamBuffer, false)
        _streamBuffer = ''
      }
      _flushTimer = null
    }, STREAM_FLUSH_INTERVAL)
  }
}

/**
 * 流式结束：刷缓冲 + 通知 stream-tts 结束
 */
export function flushStream() {
  if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null }
  if (_streamBuffer) {
    _sendTextToTTS(_streamBuffer, false)
    _streamBuffer = ''
  }
  _sendTextToTTS('', true)
  console.log('[CloudSDK] 文本流结束，等待音频回传...')
}

// ─── 打断 & 关闭 ───

export function interruptAndClear() {
  _audioFinalized = true
  const IVH = getIVH()
  if (IVH && _ready) {
    try { IVH.stop() } catch (_) {}
  }
  _textSeq = 0
  _audioSeq = 0
  _streamBuffer = ''
  if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null }
  if (_ttsHandle) { try { _ttsHandle.close() } catch (_) {} _ttsHandle = null }
  _ttsConnecting = false
  _ttsPending.length = 0
}

export async function close() {
  interruptAndClear()
  const IVH = getIVH()
  if (!IVH) return
  try { await IVH.closeSession() } catch (e) { console.warn('[CloudSDK] close error:', e) }
  _ready = false
  _sessionId = ''
  console.log('[CloudSDK] session closed')
}

// ─── 工具方法 ───

export function on(event, callback) {
  const IVH = getIVH()
  if (IVH) IVH.on(event, callback)
}

export function setVolume(val) {
  const IVH = getIVH()
  if (IVH) IVH.setVolume(val)
}

export function reconnect() {
  const IVH = getIVH()
  if (IVH) IVH.reconnect()
}

export function info() {
  const IVH = getIVH()
  return IVH ? IVH.info() : null
}

export function isReady() { return _ready }
export function isStreamActive() { return _ttsHandle !== null || _ttsConnecting }
export function isBusy() {
  return isStreamActive() || (_lastAudioAt > 0 && Date.now() - _lastAudioAt < PLAYBACK_GRACE_MS)
}
export function getSessionId() { return _sessionId }
